/**
 * The settings registry — `/v1/admin/settings*`.
 *
 * **The `GET /v1/admin/settings` feed *is* the render model** (doc 27: no UI-side catalogue). Every
 * setting arrives carrying its own label, description, type, bounds, unit, source, which scope may
 * edit it, whether it is safety-locked and whether a change needs a second approver. The console
 * renders what the registry declares — it does not keep a parallel list of settings, because a
 * parallel list is a list that goes out of date silently and then hides a setting nobody can find.
 *
 * `PUT` returns **either** the updated row **or** an approval record: a change to a setting that
 * requires a second approver does not take effect, it parks. Those are different outcomes and the
 * screen has to tell them apart rather than reporting "saved" for both.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type SettingRow = components['schemas']['SettingRow'];
export type SettingDetail = components['schemas']['SettingDetailResponse'];
type SettingList = components['schemas']['SettingListResponse'];
export type ApprovalOut = components['schemas']['ApprovalOut'];
type ApprovalList = components['schemas']['ApprovalListResponse'];
export type RevisionRow = components['schemas']['RevisionRow'];
type RevisionList = components['schemas']['RevisionListResponse'];
type DeprecationList = components['schemas']['DeprecatedSurfaceListResponse'];

export type WriteOutcome = SettingRow | ApprovalOut;

/** A parked change carries an approval id; an applied one carries the setting's key. */
export function isParked(outcome: WriteOutcome): outcome is ApprovalOut {
  return !('key' in outcome);
}

/**
 * The whole registry in one call, deliberately. `?group=` exists on the endpoint, but the screen's
 * group tabs are built from what the feed declares — fetching per group would mean the tab list
 * came from somewhere other than the data behind the tabs, which is the stale-catalogue bug in
 * smaller print. The registry is a few dozen rows; this is one request either way.
 */
export function useSettings() {
  return useQuery({
    queryKey: keys.settings.list(undefined),
    queryFn: () => authedClient.request<SettingList>('/v1/admin/settings'),
  });
}

export function useSetting(key: string | null) {
  return useQuery({
    queryKey: keys.settings.one(key ?? ''),
    enabled: key !== null,
    queryFn: () => authedClient.request<SettingDetail>(`/v1/admin/settings/${encodeURIComponent(key!)}`),
  });
}

/**
 * The console's urgency window in minutes (`dispatch.urgent_window_minutes`, docs/05-admin/03
 * §5.6 and §10). Undefined until the read lands, or if it fails or comes back without a number: a countdown then draws without
 * the urgency colour rather than against a number the console made up.
 */
export function useUrgentWindowMinutes(): number | undefined {
  const value = useSetting('dispatch.urgent_window_minutes').data?.setting?.value;
  return typeof value === 'number' ? value : undefined;
}

/**
 * How often the dispatch board re-reads its bookings, in milliseconds (`ops.board_poll_seconds`,
 * admin doc 08 §4). Undefined until the read lands, or if it fails: the board then does not poll
 * rather than polling at a rate the console made up.
 */
export function useBoardPollMs(): number | undefined {
  const value = useSetting('ops.board_poll_seconds').data?.setting?.value;
  return typeof value === 'number' ? value * 1000 : undefined;
}

/**
 * The newest changes across every setting — "what changed?" (doc 41 §9). Under the `settings`
 * namespace, so a write invalidates it along with everything else.
 */
export function useRecentSettingRevisions(limit: number) {
  return useQuery({
    queryKey: keys.settings.recent(),
    queryFn: () => authedClient.request<RevisionList>(`/v1/admin/settings/revisions?limit=${limit}`),
  });
}

function useSettingMutation<TArgs>(fn: (args: TArgs) => Promise<WriteOutcome>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    /* The whole namespace: a written value changes the row, its detail and its revisions, and any
       group listing it appears in. */
    onSuccess: () => client.invalidateQueries({ queryKey: keys.settings.all() }),
  });
}

export function useWriteSetting() {
  return useSettingMutation(({ key, value, reason }: { key: string; value: unknown; reason: string }) =>
    authedClient.request<WriteOutcome>(`/v1/admin/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { value, reason },
    }),
  );
}

/** Reverting is a `DELETE` of the override, not a write of the default — the default may move. */
export function useRevertSetting() {
  return useSettingMutation(({ key, reason }: { key: string; reason: string }) =>
    authedClient.request<SettingRow>(`/v1/admin/settings/${encodeURIComponent(key)}`, {
      method: 'DELETE',
      body: { reason },
    }),
  );
}

/**
 * The parked four-eyes changes, and the second half of the gate.
 *
 * These land together for a reason: until `GET /v1/admin/settings/approvals` existed, an
 * `approval_id` was discoverable only by the person who requested the change — who is the one
 * admin forbidden from approving it. The gate could be opened and never closed, and a button
 * needing an id nobody could obtain would have been a dead button (CSR-022, now resolved).
 *
 * **The requester can never be the approver**, and that is the server's check, not this one. The
 * screen shows the refusal; it does not pre-empt it, because a client that decided who was allowed
 * would be a second copy of a control that only counts once.
 */
export function useSettingApprovals() {
  return useQuery({
    queryKey: keys.settings.approvals(),
    queryFn: () => authedClient.request<ApprovalList>('/v1/admin/settings/approvals'),
  });
}

export function useApproveSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, approvalId }: { key: string; approvalId: number }) =>
      authedClient.request<SettingRow>(`/v1/admin/settings/${encodeURIComponent(key)}/approve/${approvalId}`, {
        method: 'POST',
      }),
    /* Applying a parked change moves the registry too, not just the queue. */
    onSuccess: () => client.invalidateQueries({ queryKey: keys.settings.all() }),
  });
}

/** API surfaces on the deprecation ladder (arch-16 §13). The dates live in code, so this only reads. */
export function useDeprecations() {
  return useQuery({
    queryKey: keys.settings.deprecations(),
    queryFn: () => authedClient.request<DeprecationList>('/v1/admin/settings/deprecations'),
  });
}
